// Le plan du produit en une table : à quelle adresse mène chaque module du
// rail et chaque onglet des deux portées. C'est la seule source des liens
// internes ; ni les pages ni le routeur n'en gardent de copie.
//
// Les tables sont exhaustives par construction : oublier un onglet ou en
// renommer un sans toucher sa route ne compile pas. Depuis le lot D6, tous
// les écrans de la coquille sont branchés sur de vraies données et vivent
// sous /w/:wId ; le labo ne garde que la page des briques et, en
// développement, sa connexion.

import type {
  OngletCabinet,
  OngletDossier,
} from "@app/components/compta/ui/BandeOnglets";
import type { ComptaModule } from "@app/components/compta/ui/ComptaRail";
import { ROUTE_CONNEXION } from "@app/lib/compta/connexion/porte";

// Les écrans hors coquille : l'entrée dans le produit et la page des briques.
// L'adresse de la connexion est définie dans `lib/compta/connexion/porte.ts`,
// avec la décision qui l'utilise : un seul endroit sait où est la porte.
export const ROUTES_ENTREE = {
  connexion: ROUTE_CONNEXION,
  briques: "/labo-briques",
};

// ————— Les routes PRODUIT, sous /w/:wId —————

const MODULES_PRODUIT: Record<ComptaModule, string> = {
  // Le module « portefeuille » atterrit sur la liste des dossiers, la porte
  // d'entrée du cabinet.
  portefeuille: "dossiers",
  clients: "clients",
  relances: "relances",
  parametres: "parametres",
  // L'échéancier (série 2, lot D4) : les échéances dérivées des profils.
  echeancier: "echeancier",
  // Les Tableaux de bord (série 2, lot D5a) : l'activité sur de vraies données.
  "tableaux-de-bord": "tableaux-de-bord",
};

const ONGLETS_CABINET_PRODUIT: Record<OngletCabinet, string> = {
  dossiers: "dossiers",
  procedures: "procedures",
  // Le kanban des révisions (série 2, lot D5b).
  travaux: "travaux",
  // Les pièces de tous les dossiers (série 2, lot D6).
  fichiers: "fichiers",
};

const ONGLETS_DOSSIER_PRODUIT: Record<OngletDossier, string> = {
  apercu: "apercu",
  cahier: "cahier",
  revue: "revue",
  fichiers: "fichiers",
  cloture: "cloture",
  tva: "tva",
};

// Les listes que la coquille parcourt (le rail, les deux bandes d'onglets),
// dans l'ordre des tables ci-dessus.
export const MODULES_COMPTA: ComptaModule[] = [
  "portefeuille",
  "echeancier",
  "relances",
  "tableaux-de-bord",
  "clients",
  "parametres",
];
export const ONGLETS_CABINET: OngletCabinet[] = [
  "dossiers",
  "procedures",
  "travaux",
  "fichiers",
];
export const ONGLETS_DOSSIER: OngletDossier[] = [
  "apercu",
  "cahier",
  "revue",
  "tva",
  "fichiers",
  "cloture",
];

export function routeProduitModule(wId: string, module: ComptaModule): string {
  return `/w/${wId}/${MODULES_PRODUIT[module]}`;
}

export function routeProduitOngletCabinet(
  wId: string,
  onglet: OngletCabinet
): string {
  return `/w/${wId}/${ONGLETS_CABINET_PRODUIT[onglet]}`;
}

/** L'adresse copie celle de l'API : `/w/:wId/spaces/:spaceId/<onglet>`. */
export function routeProduitDossier(
  wId: string,
  spaceId: string,
  onglet: OngletDossier
): string {
  return `/w/${wId}/spaces/${spaceId}/${ONGLETS_DOSSIER_PRODUIT[onglet]}`;
}

export function routeProduitPortefeuille(wId: string): string {
  return `/w/${wId}/dossiers`;
}

/** The first steps of a new cabinet's associé (série 2, lots C3+C4). */
export function routeProduitPremiersPas(wId: string): string {
  return `/w/${wId}/premiers-pas`;
}
